var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import Modal from '@widget/popups/Modal';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import ErrorState from '@helper/ErrorState';
import Icon from '../../../icons/Icon';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
/**
 * @export
 * Takes a photo with the device camera, then shows it with retake and "Use photo".
 * Phones open their own camera app, so the photo is the phone's full-quality photo.
 * Elsewhere it shows a live preview (landscape, up to 1080p) and saves the frame shown, full size,
 * as a JPEG `File`. Switches cameras when there are several.
 * When the camera can't be used (not https, access blocked, no camera) it shows why, with
 * "Try again" and, when `onChooseFile` is given, "Choose from this device".
 *
 * Opens as a dialog (full screen on phones), or renders `inline` inside another component.
 *
 * @example
 * ```tsx
 * <CameraCapture show={open} onClose={() => setOpen(false)} onCapture={async (file) => { await upload(file); setOpen(false); }} />
 * ```
 *
 * @category Inputs/Pickers/Media
 */
const CameraCapture = (props) => {
    const { show, inline, onCapture, onClose, onChooseFile, facingMode = 'user', title, className } = props;
    const context = useContext(UXPContext);
    const $L = (key) => { var _a, _b; return (_b = (_a = context === null || context === void 0 ? void 0 : context.$L) === null || _a === void 0 ? void 0 : _a.call(context, key)) !== null && _b !== void 0 ? _b : key; };
    const active = inline || !!show;
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [facing, setFacing] = useState(facingMode);
    // The cameras the browser reports (after permission) and the one in use; switching
    // cycles through them, since many devices ignore the front/back hint
    const [cameras, setCameras] = useState([]);
    const [cameraId, setCameraId] = useState(null);
    // Phones use their own camera app (full quality, own switch); no live preview here
    const native = useIsMobileLayout();
    const canSwitch = cameras.length > 1;
    const inputRef = useRef(null);
    const autoOpened = useRef(false);
    const [error, setError] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [saving, setSaving] = useState(false);
    const [attempt, setAttempt] = useState(0);
    // Front cameras (all webcams) start mirrored, like Photo Booth and the iPhone front camera;
    // back cameras don't. The Mirror button toggles it; the photo always matches the live view
    const [mirror, setMirror] = useState(true);
    const stop = useCallback(() => {
        var _a;
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach(t => t.stop());
        streamRef.current = null;
    }, []);
    // The video may mount before or after the stream arrives
    const attachVideo = useCallback((el) => {
        videoRef.current = el;
        if (el && streamRef.current)
            el.srcObject = streamRef.current;
    }, []);
    // Phones: open the camera app once as soon as the camera is shown (still inside the user's
    // tap). A browser that blocks it leaves the "Open camera" button, which always works.
    const openNative = () => {
        if (!inputRef.current)
            return;
        inputRef.current.value = '';
        inputRef.current.click();
    };
    useEffect(() => {
        if (!active) {
            autoOpened.current = false;
            return;
        }
        if (native && !autoOpened.current) {
            autoOpened.current = true;
            openNative();
        }
    }, [active, native]);
    const onNativePhoto = (e) => {
        var _a;
        const file = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (file)
            setSnapshot({ file, url: URL.createObjectURL(file) });
    };
    useEffect(() => {
        var _a;
        if (!active || snapshot || native)
            return;
        if (!window.isSecureContext || !((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia)) {
            setError(window.isSecureContext ? 'none' : 'insecure');
            return;
        }
        let cancelled = false;
        setError(null);
        // Without a size the browser picks a small default (often 640 x 480). 1080p landscape is
        // the usual webcam maximum; larger asks can land on odd portrait modes on Mac cameras
        const best = { width: { ideal: 1920 }, height: { ideal: 1080 }, aspectRatio: { ideal: 16 / 9 } };
        navigator.mediaDevices.getUserMedia({ video: cameraId ? Object.assign(Object.assign({}, best), { deviceId: { exact: cameraId } }) : Object.assign(Object.assign({}, best), { facingMode: facing }) })
            .then(stream => {
            var _a, _b, _c;
            // Closed or captured before the camera answered: release it at once
            if (cancelled)
                return stream.getTracks().forEach(t => t.stop());
            streamRef.current = stream;
            setMirror(((_a = stream.getVideoTracks()[0]) === null || _a === void 0 ? void 0 : _a.getSettings().facingMode) !== 'environment');
            if (videoRef.current)
                videoRef.current.srcObject = stream;
            (_c = (_b = navigator.mediaDevices).enumerateDevices) === null || _c === void 0 ? void 0 : _c.call(_b).then(devices => { if (!cancelled)
                setCameras(devices.filter(d => d.kind === 'videoinput' && d.deviceId).map(d => d.deviceId)); }).catch(() => { });
        })
            .catch((e) => {
            if (cancelled)
                return;
            setError((e === null || e === void 0 ? void 0 : e.name) === 'NotAllowedError' || (e === null || e === void 0 ? void 0 : e.name) === 'SecurityError' ? 'blocked'
                : (e === null || e === void 0 ? void 0 : e.name) === 'NotFoundError' || (e === null || e === void 0 ? void 0 : e.name) === 'OverconstrainedError' ? 'none' : 'failed');
        });
        return () => { cancelled = true; stop(); };
    }, [active, snapshot, native, facing, cameraId, attempt, stop]);
    // The next camera: by device when the browser lists them, else the other facing
    const switchCamera = () => {
        var _a, _b;
        if (cameras.length > 1) {
            const current = cameraId || ((_b = (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getVideoTracks()[0]) === null || _b === void 0 ? void 0 : _b.getSettings().deviceId) || cameras[0];
            setCameraId(cameras[(cameras.indexOf(current) + 1) % cameras.length]);
        }
        else {
            setCameraId(null);
            setFacing(f => f === 'user' ? 'environment' : 'user');
        }
    };
    // Release the photo's object URL when it is replaced or the camera closes
    useEffect(() => () => { if (snapshot)
        URL.revokeObjectURL(snapshot.url); }, [snapshot]);
    const reset = () => { stop(); setSnapshot(null); setError(null); setSaving(false); };
    const close = () => { reset(); onClose === null || onClose === void 0 ? void 0 : onClose(); };
    // The frame shown, at the stream's full size: the photo always matches the live view
    // (ImageCapture.takePhoto uses the camera's still mode, whose shape and framing differ)
    const capture = () => {
        const video = videoRef.current;
        if (!(video === null || video === void 0 ? void 0 : video.videoWidth))
            return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        if (mirror) {
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(blob => {
            if (!blob)
                return;
            const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
            stop();
            setSnapshot({ file, url: URL.createObjectURL(file) });
        }, 'image/jpeg', 0.92);
    };
    const usePhoto = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!snapshot)
            return;
        setSaving(true);
        try {
            yield onCapture(snapshot.file);
        }
        finally {
            setSaving(false);
        }
    });
    const errorText = {
        insecure: $L('uxp-core.camera.insecure'),
        blocked: $L('uxp-core.camera.blocked'),
        none: $L('uxp-core.camera.none'),
        failed: $L('uxp-core.camera.failed'),
    };
    const body = _jsx("div", { className: classNames('uxpcore_cameracapture', { 'uxpcore_cameracapture--inline': inline }, className), children: error
            ? _jsx(ErrorState, { variant: "error", icon: "fal camera-slash", title: $L('uxp-core.camera.unavailable'), description: errorText[error], descriptionDisplay: "inline", action: _jsxs("div", { className: "uxpcore_cameracapture__actions", children: [error !== 'insecure' && _jsx(ButtonComponent, { title: $L('uxp-core.text.try-again'), variant: "secondary", onClick: () => setAttempt(n => n + 1) }), onChooseFile && _jsx(ButtonComponent, { title: $L('uxp-core.camera.choose-file'), leftIcon: "fal image", onClick: onChooseFile })] }) })
            : _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_cameracapture__frame", children: snapshot
                            ? _jsx("img", { className: "uxpcore_cameracapture__media", src: snapshot.url, alt: "" })
                            : native
                                ? _jsx("button", { type: "button", className: "uxpcore_cameracapture__open", "aria-label": $L('uxp-core.camera.open'), onClick: openNative, children: _jsx(Icon, { icon: ['fal', 'camera'] }) })
                                : _jsx("video", { ref: attachVideo, className: classNames('uxpcore_cameracapture__media', { 'uxpcore_cameracapture__media--mirrored': mirror }), autoPlay: true, playsInline: true, muted: true }) }), _jsx("div", { className: "uxpcore_cameracapture__actions", children: snapshot
                            ? _jsxs(_Fragment, { children: [_jsx(ButtonComponent, { title: $L('uxp-core.camera.retake'), leftIcon: "fal rotate-left", variant: "secondary", disabled: saving, onClick: () => { setSnapshot(null); if (native)
                                            openNative(); } }), _jsx(ButtonComponent, { title: $L('uxp-core.camera.use-photo'), leftIcon: "fal check", loading: saving, disabled: saving, onClick: usePhoto })] })
                            : native
                                ? _jsxs(_Fragment, { children: [onChooseFile && _jsx(ButtonComponent, { title: $L('uxp-core.camera.choose-file'), leftIcon: "fal image", variant: "secondary", onClick: onChooseFile }), _jsx(ButtonComponent, { title: $L('uxp-core.camera.open'), leftIcon: "fal camera", onClick: openNative })] })
                                : _jsxs(_Fragment, { children: [_jsx(ButtonComponent, { title: $L('uxp-core.camera.mirror'), leftIcon: "fal arrows-left-right", variant: "secondary", active: mirror, onClick: () => setMirror(m => !m) }), canSwitch && _jsx(ButtonComponent, { className: "uxpcore_cameracapture__switch", title: $L('uxp-core.camera.switch'), leftIcon: "fal camera-rotate", variant: "secondary", onClick: switchCamera }), _jsx(ButtonComponent, { title: $L('uxp-core.camera.capture'), leftIcon: "fal camera", onClick: capture })] }) })] }) });
    // Kept outside the dialog so it exists when the camera is first shown
    const nativeInput = native && _jsx("input", { ref: inputRef, type: "file", accept: "image/*", capture: facing, hidden: true, onChange: onNativePhoto });
    if (inline)
        return _jsxs(_Fragment, { children: [nativeInput, body] });
    return _jsxs(_Fragment, { children: [nativeInput, _jsx(Modal, { show: !!show, onClose: close, title: title || $L('uxp-core.camera.take-photo'), width: "40rem", children: show && body })] });
};
export default CameraCapture;
